import { useEffect, useState } from 'preact/hooks';
import { api } from '../api/client';
import { route } from 'preact-router';
import { T } from '../styles/tokens';
import { ListSkeleton, SkeletonBar, Button } from '../components/ui';
// Componentes ui não usados aqui — tabs e filtros são muito customizados (estilo IDE)

interface FrontendRoute {
  path: string;
  component: string;
  group: 'public' | 'admin' | 'cloud' | 'user' | 'dev';
  hasMenuLink: boolean;
  requiresAuth: boolean;
}

interface ApiRoute {
  method: string;
  path: string;
  handler: string;
  group: string;
}

interface SitemapData {
  frontend_routes: FrontendRoute[];
  api_routes: ApiRoute[];
  stats: {
    total_frontend: number;
    total_api: number;
    frontend_no_menu: number;
    frontend_by_group: Record<string, number>;
  };
}

const GROUP_COLORS: Record<string, string> = {
  admin: T.primary,
  cloud: T.purple,
  user: T.success,
  public: T.textMuted,
  dev: T.warning,
  auth: T.danger,
};

const METHOD_COLORS: Record<string, string> = {
  GET: T.success,
  POST: T.primary,
  PUT: T.warning,
  PATCH: T.warning,
  DELETE: T.danger,
};

const FONT = "'Cascadia Code','Fira Code','SF Mono',Consolas,monospace";

export function AdminSitemap() {
  const [data, setData] = useState<SitemapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<'frontend' | 'api'>('frontend');
  const [search, setSearch] = useState('');
  const [groupFilter, setGroupFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');

  useEffect(() => {
    api.get('/admin/sitemap')
      .then((r: any) => { setData(r.data || r); setLoading(false); })
      .catch((e: any) => { setError(e.message); setLoading(false); });
  }, []);

  if (loading) return <div style={{ padding: 40, fontFamily: FONT }}><ListSkeleton rows={4} /></div>;
  if (error || !data) return <div style={{ padding: 40, color: T.danger, fontFamily: FONT, fontSize: 13 }}>Erro: {error}</div>;

  const filteredFrontend = data.frontend_routes.filter(r => {
    if (groupFilter !== 'all' && r.group !== groupFilter) return false;
    if (search && !r.path.toLowerCase().includes(search.toLowerCase()) && !r.component.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const filteredApi = data.api_routes.filter(r => {
    if (methodFilter !== 'all' && r.method !== methodFilter) return false;
    if (groupFilter !== 'all' && r.group !== groupFilter) return false;
    if (search && !r.path.toLowerCase().includes(search.toLowerCase()) && !r.handler.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const inputStyle: any = {
    background: T.bgSecondary, border: `1px solid ${T.border}`, borderRadius: 3,
    padding: '6px 10px', color: T.text, fontSize: 12, fontFamily: FONT, outline: 'none',
  };

  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      background: T.bg, color: T.text, fontFamily: FONT, fontSize: 13,
    }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px',
        height: 40, background: T.bg, borderBottom: `1px solid ${T.border}`, flexShrink: 0,
      }}>
        {/* Tabs */}
        {(['frontend', 'api'] as const).map(t => (
          <Button variant="ghost" key={t} onClick={() => setTab(t)} style={{
            background: 'transparent', border: 'none', borderBottom: tab === t ? `2px solid ${T.primary}` : '2px solid transparent',
            color: tab === t ? '#fff' : T.textMuted, fontSize: 12, fontFamily: FONT, fontWeight: tab === t ? 600 : 400,
            padding: '0 8px', height: '100%', cursor: 'pointer',
          }}>
            {t === 'frontend' ? `Frontend (${data.stats.total_frontend})` : `API (${data.stats.total_api})`}
          </Button>
        ))}

        <div style={{ flex: 1 }} />

        {/* Search */}
        <input
          type="text"
          placeholder="Buscar..."
          value={search}
          onInput={(e: any) => setSearch(e.target.value)}
          style={{ ...inputStyle, width: 200 }}
        />

        {/* Group filter */}
        <select value={groupFilter} onChange={(e: any) => setGroupFilter(e.target.value)} style={inputStyle}>
          <option value="all">Todos os Grupos</option>
          <option value="admin">admin</option>
          <option value="cloud">cloud</option>
          <option value="user">user</option>
          <option value="public">public</option>
          <option value="dev">dev</option>
        </select>

        {/* Method filter (API only) */}
        {tab === 'api' && (
          <select value={methodFilter} onChange={(e: any) => setMethodFilter(e.target.value)} style={inputStyle}>
            <option value="all">All Methods</option>
            <option value="GET">GET</option>
            <option value="POST">POST</option>
            <option value="PUT">PUT</option>
            <option value="PATCH">PATCH</option>
            <option value="DELETE">DELETE</option>
          </select>
        )}
      </div>

      {/* Stats bar */}
      <div style={{
        display: 'flex', gap: 16, padding: '6px 16px', background: T.bg,
        borderBottom: `1px solid ${T.border}`, fontSize: 11, color: T.textMuted, flexShrink: 0,
      }}>
        <span>{data.stats.total_frontend} frontend</span>
        <span>{data.stats.total_api} api</span>
        <span style={{ color: data.stats.frontend_no_menu > 0 ? T.warning : T.textMuted }}>
          {data.stats.frontend_no_menu} no menu
        </span>
        <span>{data.stats.total_frontend + data.stats.total_api} total</span>
        <div style={{ flex: 1 }} />
        <span>{tab === 'frontend' ? filteredFrontend.length : filteredApi.length} results</span>
      </div>

      {/* Table */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: T.bgSecondary }}>
              {tab === 'frontend' ? (
                <>
                  <th style={thStyle}>PATH</th>
                  <th style={thStyle}>COMPONENT</th>
                  <th style={thStyle}>GROUP</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>MENU</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>AUTH</th>
                </>
              ) : (
                <>
                  <th style={{ ...thStyle, width: 80 }}>METHOD</th>
                  <th style={thStyle}>PATH</th>
                  <th style={thStyle}>HANDLER</th>
                  <th style={thStyle}>GROUP</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {tab === 'frontend' && filteredFrontend.map((r, i) => (
              <tr key={r.path} style={{ background: i % 2 === 0 ? T.bg : T.bgCard }}
                onMouseEnter={(e: any) => e.currentTarget.style.background = T.bgCard}
                onMouseLeave={(e: any) => e.currentTarget.style.background = i % 2 === 0 ? T.bg : T.bgCard}>
                <td style={tdStyle}>
                  <a onClick={() => route(r.path.replace(/:[^/]+/g, '_'))}
                    style={{ color: T.primary, cursor: 'pointer', textDecoration: 'none' }}
                    onMouseEnter={(e: any) => e.currentTarget.style.textDecoration = 'underline'}
                    onMouseLeave={(e: any) => e.currentTarget.style.textDecoration = 'none'}>
                    {r.path}
                  </a>
                </td>
                <td style={{ ...tdStyle, color: T.textMuted }}>{r.component}</td>
                <td style={tdStyle}>
                  <span style={badgeStyle(GROUP_COLORS[r.group] || T.textMuted)}>{r.group}</span>
                </td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>{r.hasMenuLink ? '' : '—'}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>{r.requiresAuth ? '' : '—'}</td>
              </tr>
            ))}
            {tab === 'api' && filteredApi.map((r, i) => (
              <tr key={`${r.method}-${r.path}-${i}`} style={{ background: i % 2 === 0 ? T.bg : T.bgCard }}
                onMouseEnter={(e: any) => e.currentTarget.style.background = T.bgCard}
                onMouseLeave={(e: any) => e.currentTarget.style.background = i % 2 === 0 ? T.bg : T.bgCard}>
                <td style={tdStyle}>
                  <span style={badgeStyle(METHOD_COLORS[r.method] || T.textMuted)}>{r.method}</span>
                </td>
                <td style={tdStyle}>{r.path}</td>
                <td style={{ ...tdStyle, color: T.textMuted }}>{r.handler}</td>
                <td style={tdStyle}>
                  <span style={badgeStyle(GROUP_COLORS[r.group] || T.textMuted)}>{r.group}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const thStyle: any = {
  textAlign: 'left', padding: '8px 12px', color: T.textMuted,
  fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px',
  borderBottom: `1px solid ${T.border}`, position: 'sticky' as const, top: 0, background: T.bgSecondary,
};

const tdStyle: any = {
  padding: '6px 12px', fontSize: 12, color: T.text, borderBottom: `1px solid ${T.border}`,
};

function badgeStyle(color: string): any {
  return {
    display: 'inline-block', padding: '1px 6px', borderRadius: 3,
    fontSize: 10, fontWeight: 600, textTransform: 'uppercase',
    color, border: `1px solid ${color}33`, background: `${color}11`,
  };
}
